import type {HalContext} from "@cm/hal"
import type {WebGl2ImageVirtualMipmapInfo} from "#webgl2/webgl2-image-virtual-mipmap-info"
import type {PageHandle} from "#webgl2/webgl2-image-atlas"
import type {HalGeometryRef} from "@cm/hal/hal-geometry/index"

function packPageTableEntry(atlasX: number, atlasY: number, atlasIndex: number, coarsenedLevels: number, isValid: number): number {
    const buf = new Uint32Array(1)
    buf[0] = 0x00800000 | ((atlasIndex & 0x7) << 20) | ((coarsenedLevels & 0xf) << 16) | ((atlasY & 0xff) << 8) | (atlasX & 0xff)
    if (isValid) buf[0] |= 0x80000000
    return new Float32Array(buf.buffer)[0]
}

export class WebGl2ImageVirtualPageTableUpdates {
    constructor(
        private context: HalContext,
        private mipmapInfo: WebGl2ImageVirtualMipmapInfo,
        private numChannels: number,
    ) {
        this.geometry = this.context.createGeometry()
    }

    dispose() {
        this.geometry?.release()
        this.geometry = null
    }

    add(linearPageIndex: number, pageHandleByChannel: PageHandle[] | null) {
        if (pageHandleByChannel && pageHandleByChannel.length !== this.numChannels) {
            throw new Error(`Invalid number of page handles: ${pageHandleByChannel.length}, expected: ${this.numChannels}`)
        }
        this.pageHandlesByPageLookupTexelIndex.set(linearPageIndex, pageHandleByChannel)
    }

    flushToGeometry() {
        if (!this.geometry) {
            throw new Error("Disposed")
        }
        this.geometry.ref.clear()
        for (const [lookupTexelIndex, pageHandles] of this.pageHandlesByPageLookupTexelIndex) {
            const lastCommittedPageHandles = this.lastCommittedPageHandlesByPageLookupTexelIndex.get(lookupTexelIndex)
            for (let i = 0; i < this.numChannels; i++) {
                const pageIndex = this.mipmapInfo.getPageLookupTexel(lookupTexelIndex, i)
                const pageHandle = pageHandles?.[i]
                const lastCommittedPageHandle = lastCommittedPageHandles?.[i]
                if (pageHandle === lastCommittedPageHandle) {
                    // no change
                    continue
                }
                // add rect for this page
                const packed = pageHandle ? packPageTableEntry(pageHandle.pageIndex.x, pageHandle.pageIndex.y, pageHandle.atlasId, 0, 1) : 0.0
                this.geometry.ref.addRect(
                    {
                        x: pageIndex.x,
                        y: pageIndex.y,
                        width: 1,
                        height: 1,
                    },
                    undefined,
                    {r: packed, g: 0, b: 0, a: 0},
                )
            }
            this.lastCommittedPageHandlesByPageLookupTexelIndex.set(lookupTexelIndex, pageHandles)
        }
        this.pageHandlesByPageLookupTexelIndex.clear()
        return this.geometry
    }

    private pageHandlesByPageLookupTexelIndex = new Map<number, PageHandle[] | null>()
    private lastCommittedPageHandlesByPageLookupTexelIndex = new Map<number, PageHandle[] | null>()
    private geometry: HalGeometryRef | null
}
